import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faChevronDown,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

export type DSChipSize = "sm" | "md";
export type DSChipVariant = "filter" | "selectable";

export interface DSChipProps {
  children: React.ReactNode;
  size?: DSChipSize;
  /**
   * Chip behavior variant:
   * - "filter": Shows chevron when not applied, typically opens a dropdown on click
   * - "selectable": No chevron, clicking toggles selection, X to clear when applied
   */
  variant?: DSChipVariant;
  /**
   * When true, shows as filled with dismiss icon (if onDismiss provided).
   */
  applied?: boolean;
  /**
   * Callback when dismiss icon is clicked (only shown when applied=true).
   */
  onDismiss?: () => void;
  /**
   * Callback when chip is clicked.
   * - For filter chips: opens dropdown/overlay
   * - For selectable chips: toggles selection
   */
  onClick: () => void;
  /** Optional leading icon */
  icon?: IconProp;
  disabled?: boolean;
  /** Accessible label for screen readers */
  "aria-label"?: string;
  dataTestId: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * DSChip - Interactive chip component for filters and selectable tags
 *
 * Variants:
 * - "filter": Shows chevron, clicking opens dropdown/overlay (default)
 * - "selectable": No chevron, clicking toggles selection
 *
 * For non-interactive status display, use DSBadge instead.
 */

export const DSChip = ({
  children,
  size = "md",
  variant = "filter",
  applied = false,
  disabled = false,
  onDismiss,
  onClick,
  icon,
  "aria-label": ariaLabel,
  dataTestId,
  ref,
}: DSChipProps) => {
  // Base styles
  const baseStyles = `inline-flex items-center gap-1 rounded-full border transition-colors focus:outline-none ${
    disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
  }`;

  // Size styles
  const sizeStyles = {
    sm: "h-6 px-sm text-xs",
    md: "h-8 px-md text-sm",
  };

  // State styles - brand color only for simplicity
  const stateStyles = applied
    ? "bg-brand text-white border-brand hover:bg-[var(--surface-brand-hover)]"
    : "bg-transparent text-brand border-brand hover:bg-[var(--surface-brand-subtle-hover)]";

  const handleClick = () => {
    if (disabled) return;
    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    onDismiss?.();
  };

  const handleDismissKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onDismiss?.();
    }
  };

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      aria-pressed={applied}
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
      className={`${baseStyles} ${sizeStyles[size]} ${stateStyles}`}
    >
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          className="flex items-center"
          aria-hidden="true"
        />
      )}
      <span className="font-body font-regular whitespace-nowrap">
        {children}
      </span>
      {applied && onDismiss ? (
        <button
          type="button"
          onClick={handleDismiss}
          onKeyDown={handleDismissKeyDown}
          disabled={disabled}
          className="flex items-center hover:bg-opacity-80 rounded-full transition-opacity"
          aria-label="Clear selection"
        >
          <FontAwesomeIcon icon={faXmark} className="text-xs" />
        </button>
      ) : (
        variant === "filter" &&
        !applied && (
          <FontAwesomeIcon
            icon={faChevronDown}
            className="text-xs"
            aria-hidden="true"
          />
        )
      )}
    </div>
  );
};

DSChip.displayName = "DSChip";
